import { useState, useEffect } from 'react';
import { Grid, Typography, TextField, Fab, Box, Alert, CircularProgress } from '@mui/material';
import { DataGridPro, GridColDef, gridClasses } from '@mui/x-data-grid-pro';
import { colors } from 'src/components/App/colors';
import { useVerifyGpLazyQuery } from 'src/graphql/generated';
import AddSharpIcon from '@mui/icons-material/AddSharp';
type ParticipantRow = {
  id: string;
  memberId: string;
  lastName: string;
};
type participantsListProps = {
  participants: ParticipantRow[];
  onChange: (participants: ParticipantRow[]) => void;
};
const ParticipantsList = ({ participants, onChange }: participantsListProps) => {
  const [draftUser, setDraftUser] = useState({ memberId: '', lastName: '' });
  const [error, setError] = useState<string | null>(null);
  const [checkParticipantList, { loading }] = useVerifyGpLazyQuery();

  const columns: GridColDef[] = [
    { field: 'lastName', headerName: 'Surname', flex: 1, sortable: false },
    { field: 'memberId', headerName: 'RACGP Number', flex: 1, sortable: false },
    {
      field: 'actions',
      headerName: 'Action',
      sortable: false,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{ cursor: 'pointer', color: 'red', textDecoration: 'underline' }}
          onClick={() => handleDelete(params.row.id)}
        >
          Remove
        </Typography>
      )
    }
  ];

  const handleAddUser = async () => {
    setError(null);
    if (!draftUser.memberId || !draftUser.lastName) return;
    // prevent duplicates
    const alreadyExists = participants.some((p) => p.memberId === draftUser.memberId);

    if (alreadyExists) {
      setError('Member already added');
      return;
    }
    try {
      const { data } = await checkParticipantList({
        variables: {
          memberId: draftUser.memberId.trim(),
          lastName: draftUser.lastName.trim()
        },
        fetchPolicy: 'no-cache'
      });
      const memberExists = data?.verifyGP ?? false;

      if (!memberExists) {
        setError('Member cannot be found');
        return;
      }

      const newParticipant: ParticipantRow = {
        id: draftUser.memberId,
        memberId: draftUser.memberId,
        lastName: draftUser.lastName
      };

      onChange([...participants, newParticipant]);

      // clear inputs
      setDraftUser({
        memberId: '',
        lastName: ''
      });
    } catch (e) {
      setError('Something went wrong while validating member details, please try again.');
    }
  };
  const handleDelete = (id: string) => {
    const updated = participants.filter((p) => p.id !== id);

    onChange(updated);
  };
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
        setDraftUser((prev) => ({
          ...prev,
          memberId: '',
          lastName: ''
        }));
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);
  return (
    <Grid item xs={12}>
      <Typography sx={{ color: colors?.['charcoal.700'], mb: 2 }} variant="body2">
        Add the details for any RACGP GPs who participated in this activity. The GP submitting this
        activity does not need to be added here.
      </Typography>

      <Grid container spacing={2} alignItems="center">
        <Grid item xs>
          <TextField
            fullWidth
            label="Surname"
            value={draftUser.lastName}
            onChange={(e) => setDraftUser({ ...draftUser, lastName: e.target.value })}
          />
        </Grid>
        <Grid item xs>
          <TextField
            fullWidth
            label="User ID"
            value={draftUser.memberId}
            onChange={(e) => setDraftUser({ ...draftUser, memberId: e.target.value })}
          />
        </Grid>
        <Grid item>
          <Fab
            size="small"
            sx={{ backgroundColor: 'white' }}
            onClick={handleAddUser}
            disabled={loading || !draftUser.memberId || !draftUser.lastName}
          >
            {loading ? <CircularProgress size={24} /> : <AddSharpIcon />}
          </Fab>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {participants.length > 0 && (
        <Box width="100%" mt={2}>
          <DataGridPro
            sx={{
              '.MuiDataGrid-columnSeparator': {
                display: 'none'
              },
              '.MuiDataGrid-columnHeaderTitle': {
                fontSize: '14px',
                fontWeight: '500'
              },
              // Remove focus outline from cells
              [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
                outline: 'none'
              },
              // Remove focus outline from column headers
              [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]:
                {
                  outline: 'none'
                }
            }}
            rows={participants}
            columns={columns}
            pagination={false}
            disableSelectionOnClick
            disableColumnResize
            disableColumnSelector
            disableColumnPinning
            disableMultipleColumnsFiltering
            disableColumnFilter
            disableColumnMenu
            autoHeight
            hideFooter
          />
        </Box>
      )}
    </Grid>
  );
};

export { ParticipantsList };
